import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildVisualizationHtml } from "../../../../utils/buildVisualizationHtml";
import { WebView } from "react-native-webview";
import {
  Modal,
  Pressable,
  StyleSheet,
  TextInput,
  View,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions,
  Linking,
} from "react-native";
import {
  XIcon,
  PaperPlaneTiltIcon,
  ChatCircleIcon,
  LayoutIcon,
  SparkleIcon,
  VideoCameraIcon,
} from "@phosphor-icons/react";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomMarkdown from "../../../common/general/CustomMarkdown/CustomMarkdown";
import { InlineResponseCard } from "../inlineCards/InlineResponseCard";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import ProcessingMessageBox from "../../../chat/LoadingMessage/ProcessingMessageBox";
import YoutubeVideoPlayerAdvanced, { buildYoutubeEmbedUrl } from "../../../youtube/YoutubeVideoPlayerAdvanced";
import {
  colors,
  radii,
  semanticColors,
  shadowStyles,
  spacing,
  typographyScale,
} from "../../../../styles/design-system";
import { getModalAnimationType } from "../../../../utils/getModalAnimationType";
import type { MessageItem } from "../../../../containers/chat/aiTutorMessage.types";

export type CanvasBlock =
  | {
      kind: "visualization";
      blockId: string;
      visualizationId: string;
      visualizationType: string;
      visualizationDescription?: string | null;
      content: string;
      mediaChatId?: string | null;
    }
  | {
      kind: "video";
      blockId: string;
      videoId?: string | null;
      url: string;
      title?: string | null;
      thumbnailUrl?: string | null;
      startTime?: number | null;
      endTime?: number | null;
      mediaChatId?: string | null;
    };

export type MessageVisualizationBlock = Extract<CanvasBlock, { kind: "visualization" }>;

export interface VisualizationModalProps {
  visible: boolean;
  block: CanvasBlock;
  messages: MessageItem[];
  inputText: string;
  isStreaming: boolean;
  isMediaChatReady?: boolean;
  onInputChange: (text: string) => void;
  onSend: () => void;
  onClose: () => void;
  onChoiceSelect?: (messageId: string, blockIndex: number, choiceIndex: number) => void;
}

const MOBILE_BREAKPOINT = 1024;

/** Non-YouTube web playback: use media-fragment URI when bounds exist (browser support varies). */
function directVideoSrc(url: string, startTime?: number | null, endTime?: number | null): string {
  if (startTime == null && endTime == null) return url;
  const start = startTime ?? 0;
  if (endTime != null) return `${url}#t=${start},${endTime}`;
  return start > 0 ? `${url}#t=${start}` : url;
}

const VisualizationModal = ({
  visible,
  block,
  messages,
  inputText,
  isStreaming,
  isMediaChatReady = true,
  onInputChange,
  onSend,
  onClose,
  onChoiceSelect,
}: VisualizationModalProps) => {
  const { width } = useWindowDimensions();
  const isMobile = width < MOBILE_BREAKPOINT;
  const [mobileTab, setMobileTab] = useState<"activity" | "chat">("activity");
  const scrollViewRef = useRef<ScrollView>(null);
  const prevIsStreamingRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    scrollViewRef.current?.scrollToEnd({ animated: false });
  }, []);

  useEffect(() => {
    if (!messages.length || isStreaming) return;
    scrollToBottom();
  }, [messages, isStreaming, scrollToBottom]);

  useEffect(() => {
    const wasStreaming = prevIsStreamingRef.current;
    prevIsStreamingRef.current = isStreaming;
    if (isStreaming && !wasStreaming) {
      scrollToBottom();
    }
  }, [isStreaming, scrollToBottom]);

  const builtContent = useMemo(
    () =>
      block.kind === "visualization" && block.content
        ? buildVisualizationHtml(block.visualizationType, block.content)
        : "",
    [block]
  );

  const renderVideoShell = (player: React.ReactNode) => (
    <View style={styles.videoContent}>
      <View style={styles.videoPlayerShell}>{player}</View>
    </View>
  );

  const renderMedia = () => {
    if (block.kind === "video") {
      if (block.videoId) {
        const embedUrl = buildYoutubeEmbedUrl({
          videoId: block.videoId,
          startTime: block.startTime,
          endTime: block.endTime,
          autoplay: true,
        });
        return renderVideoShell(
          Platform.OS === "web" ? (
            <iframe
              title={block.title ?? "Video"}
              src={embedUrl}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                border: "none",
                width: "100%",
                height: "100%",
              }}
            />
          ) : (
            <YoutubeVideoPlayerAdvanced
              id={block.videoId}
              startTime={block.startTime}
              endTime={block.endTime}
              autoplay
            />
          )
        );
      }

      if (Platform.OS === "web") {
        return renderVideoShell(
          <video
            src={directVideoSrc(block.url, block.startTime, block.endTime)}
            controls
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
            title={block.title ?? "Video"}
          />
        );
      }

      return (
        <View style={styles.videoFallback}>
          <CustomText
            text="Video playback is available on web."
            useTranslationText={false}
            size="s"
            style={styles.videoFallbackText}
          />
          <CustomButton
            text="Open video"
            styleType="primary"
            onPress={() => Linking.openURL(block.url)}
            accessibilityLabel="Open video in browser"
            useTranslationText={false}
            leftIcon={null}
            rightIcon={null}
          />
        </View>
      );
    }

    if (block.kind === "visualization" && builtContent) {
      return Platform.OS === "web" ? (
        <iframe
          srcDoc={builtContent}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100%",
            height: "100%",
            border: "none",
          }}
          sandbox="allow-scripts"
          title={block.visualizationType}
        />
      ) : (
        <WebView
          source={{ html: builtContent }}
          style={styles.webView}
          javaScriptEnabled={true}
          originWhitelist={["about:", "data:"]}
          onShouldStartLoadWithRequest={(request) => {
            return request.url === "about:blank" || request.url.startsWith("data:");
          }}
          allowFileAccess={false}
          allowUniversalAccessFromFileURLs={false}
        />
      );
    }

    return (
      <View style={styles.mediaEmpty}>
        <CustomText
          text="No visualization content available."
          useTranslationText={false}
          size="s"
          style={styles.mediaEmptyText}
        />
      </View>
    );
  };

  const WorkspaceIcon = block.kind === "video" ? VideoCameraIcon : SparkleIcon;

  const workspaceContent = (
    <View
      style={[
        styles.workspaceContent,
        block.kind === "video" ? styles.workspaceContentVideo : styles.workspaceContentViz,
      ]}>
      {renderMedia()}
    </View>
  );

  const workspacePanel = isMobile ? (
    workspaceContent
  ) : (
    <View style={styles.workspaceOuter}>
      <View style={styles.workspaceSurface}>
        <View style={styles.workspaceToolbar}>
          <View style={styles.workspaceToolbarLeft}>
            <WorkspaceIcon size={16} color={colors.brand[600]} />
            <CustomText
              text={
                block.kind === "video"
                  ? (block.title ?? "Video")
                  : block.visualizationType === "interactive"
                    ? "Interactive Visualization"
                    : block.visualizationType === "nonInteractive"
                      ? "Animated Visualization"
                      : "Visualization"
              }
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.workspaceTitle}
              numberOfLines={1}
            />
          </View>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close activity"
            style={styles.workspaceCloseButton}>
            <XIcon size={16} color={colors.grey[500]} />
          </Pressable>
        </View>
        {workspaceContent}
      </View>
    </View>
  );

  const chatPanel = (
    <View style={[styles.chatPanel, isMobile && styles.chatPanelMobile]}>
      <ScrollView
        ref={scrollViewRef}
        style={styles.chatScroll}
        contentContainerStyle={styles.chatScrollContent}
        showsVerticalScrollIndicator={false}
        onContentSizeChange={() => {
          if (!isStreaming) scrollToBottom();
        }}>
        {messages.length === 0 ? (
          <View style={styles.chatEmptyState}>
            {block.kind === "video" ? (
              <CustomText
                text={`${block.title ?? "This video"} — ask any questions about this video below.`}
                useTranslationText={false}
                size="s"
                style={styles.chatEmptyText}
              />
            ) : (
              <CustomMarkdown
                content={block.visualizationDescription ?? "Explore the visualization and ask any questions below."}
                enableLearningCallouts
                style={{ body: styles.chatEmptyText, paragraph: { marginTop: 0, marginBottom: 0 } }}
              />
            )}
          </View>
        ) : (
          messages.map((msg) => (
            <View
              key={msg.id}
              style={[styles.chatBubble, msg.sender === "human" ? styles.chatBubbleUser : styles.chatBubbleAI]}>
              {msg.sender === "human" ? (
                <CustomText
                  text={msg.content ?? ""}
                  useTranslationText={false}
                  size="s"
                  style={styles.chatBubbleUserText}
                />
              ) : (
                <>
                  <CustomMarkdown content={msg.content ?? ""} enableLearningCallouts />
                  {(msg.blocks ?? []).map((block, blockIndex) => {
                    if (block.kind !== "inline_response" && block.kind !== "mcq") {
                      return null;
                    }
                    return (
                      <InlineResponseCard
                        key={`${msg.id}-${block.kind}-${blockIndex}`}
                        question={block.question}
                        choices={block.choices}
                        selectedChoiceIndex={block.selectedChoiceIndex}
                        submitted={block.submitted}
                        onSelectChoice={
                          !isStreaming && isMediaChatReady && onChoiceSelect
                            ? (choiceIndex) => onChoiceSelect(msg.id, blockIndex, choiceIndex)
                            : undefined
                        }
                        colorVariant={block.kind === "mcq" ? "mcq" : "default"}
                      />
                    );
                  })}
                </>
              )}
            </View>
          ))
        )}
        {isStreaming ? (
          <View style={styles.processingBox}>
            <ProcessingMessageBox />
          </View>
        ) : null}
      </ScrollView>

      <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined}>
        <View style={styles.inputRow}>
          <TextInput
            style={styles.input}
            value={inputText}
            onChangeText={onInputChange}
            placeholder={block.kind === "video" ? "Ask about this video..." : "Ask about this visualization..."}
            placeholderTextColor={semanticColors.typography.textLight}
            multiline={false}
            onSubmitEditing={onSend}
            returnKeyType="send"
            editable={!isStreaming && isMediaChatReady}
          />
          <CustomButton
            style={[
              styles.sendButton,
              (!inputText.trim() || isStreaming || !isMediaChatReady) && styles.sendButtonDisabled,
            ]}
            onPress={onSend}
            disabled={!inputText.trim() || isStreaming || !isMediaChatReady}
            leftIcon={(<PaperPlaneTiltIcon size={16} color={colors.white} />) as any}
            rightIcon={null}
            useTranslationText={false}
            accessibilityLabel="Send message"
            accessibilityRole="button"
          />
        </View>
      </KeyboardAvoidingView>
    </View>
  );

  return (
    <Modal visible={visible} transparent={false} animationType={getModalAnimationType("fade")} onRequestClose={onClose}>
      <View style={styles.shell}>
        {isMobile && (
          <View style={styles.tabBar}>
            <View style={styles.tabBarTabs}>
              <Pressable
                style={[styles.tab, mobileTab === "chat" && styles.tabActive]}
                onPress={() => setMobileTab("chat")}>
                <ChatCircleIcon size={16} color={mobileTab === "chat" ? colors.brand[600] : colors.grey[600]} />
                <CustomText
                  text="Chat"
                  useTranslationText={false}
                  size="s"
                  weight={mobileTab === "chat" ? "bold" : "regular"}
                  style={mobileTab === "chat" ? styles.tabTextActive : styles.tabText}
                />
              </Pressable>
              <Pressable
                style={[styles.tab, mobileTab === "activity" && styles.tabActive]}
                onPress={() => setMobileTab("activity")}>
                <LayoutIcon size={16} color={mobileTab === "activity" ? colors.brand[600] : colors.grey[600]} />
                <CustomText
                  text="Activity"
                  useTranslationText={false}
                  size="s"
                  weight={mobileTab === "activity" ? "bold" : "regular"}
                  style={mobileTab === "activity" ? styles.tabTextActive : styles.tabText}
                />
              </Pressable>
            </View>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close"
              style={styles.tabBarCloseButton}>
              <XIcon size={16} color={colors.grey[500]} />
            </Pressable>
          </View>
        )}

        {isMobile ? (
          <View style={styles.bodyMobile}>
            <View style={[{ flex: 1 }, mobileTab !== "chat" && styles.hiddenPanel]}>{chatPanel}</View>
            <View style={[styles.activityPanel, mobileTab !== "activity" && styles.hiddenPanel]}>{workspacePanel}</View>
          </View>
        ) : (
          <View style={styles.body}>
            {chatPanel}
            <View style={styles.activityPanel}>{workspacePanel}</View>
          </View>
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    width: "100%",
    height: "100%",
    backgroundColor: colors.grey[100],
  },
  tabBar: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: semanticColors.surface.white,
  },
  tabBarTabs: {
    flex: 1,
    flexDirection: "row",
  },
  tabBarCloseButton: {
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radii.lg,
  },
  tab: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing.xxs,
    paddingVertical: spacing.sm,
    minHeight: 44,
  },
  tabActive: {
    borderBottomWidth: 2,
    borderBottomColor: colors.brand[600],
  },
  tabText: {
    color: colors.grey[600],
  },
  tabTextActive: {
    color: colors.brand[600],
  },
  body: {
    flex: 1,
    flexDirection: "row",
    overflow: "hidden",
  },
  bodyMobile: {
    flex: 1,
    overflow: "hidden",
  },
  hiddenPanel: {
    display: "none",
  },
  chatPanel: {
    width: "28%",
    minWidth: 300,
    backgroundColor: semanticColors.surface.white,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  chatPanelMobile: {
    width: "100%",
    flex: 1,
  },
  chatScroll: {
    flex: 1,
  },
  chatScrollContent: {
    padding: spacing.md,
    gap: spacing.sm,
  },
  chatEmptyState: {
    padding: spacing.md,
  },
  chatEmptyText: {
    color: semanticColors.typography.textSecondary,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  chatBubble: {
    borderRadius: radii.lg,
    padding: spacing.sm,
    maxWidth: "90%",
  },
  chatBubbleUser: {
    alignSelf: "flex-end",
    backgroundColor: colors.blue[600],
  },
  chatBubbleAI: {
    alignSelf: "flex-start",
    backgroundColor: semanticColors.surface.light,
  },
  chatBubbleUserText: {
    color: colors.white,
    fontSize: typographyScale.textSm.fontSize,
  },
  processingBox: {
    alignSelf: "flex-start",
    marginTop: spacing.xs,
    position: "relative",
    width: "100%",
    minHeight: 140,
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    gap: spacing.xs,
  },
  input: {
    flex: 1,
    backgroundColor: semanticColors.surface.light,
    borderRadius: radii.lg,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typographyScale.textSm.fontSize,
    color: semanticColors.typography.textPrimary,
    // @ts-expect-error - works for web
    outlineStyle: "none",
  },
  sendButton: {
    width: 36,
    height: 36,
    borderRadius: radii.full,
    backgroundColor: colors.blue[600],
    justifyContent: "center",
    alignItems: "center",
  },
  sendButtonDisabled: {
    opacity: 0.4,
  },
  activityPanel: {
    flex: 1,
    overflow: "hidden",
  },
  workspaceOuter: {
    flex: 1,
    padding: spacing.sm,
  },
  workspaceSurface: {
    flex: 1,
    backgroundColor: semanticColors.surface.white,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.04)",
    overflow: "hidden",
    ...shadowStyles.lg,
  },
  workspaceToolbar: {
    height: 48,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
    gap: spacing.sm,
  },
  workspaceToolbarLeft: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    minWidth: 0,
  },
  workspaceTitle: {
    color: colors.grey[900],
    flex: 1,
  },
  workspaceCloseButton: {
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radii.lg,
  },
  workspaceContent: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
  },
  workspaceContentViz: {
    backgroundColor: colors.grey[100],
  },
  workspaceContentVideo: {
    backgroundColor: colors.grey[900],
  },
  videoContent: {
    flex: 1,
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  videoPlayerShell: {
    aspectRatio: 16 / 9,
    maxHeight: "100%",
    maxWidth: "100%",
    ...(Platform.OS === "web"
      ? ({
          height: "100%",
          width: "auto",
        } as const)
      : {
          width: "100%",
        }),
  },
  videoFallback: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: spacing.sm,
    padding: spacing.lg,
  },
  videoFallbackText: {
    color: semanticColors.typography.textSecondary,
    textAlign: "center",
  },
  webView: {
    flex: 1,
  },
  mediaEmpty: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  mediaEmptyText: {
    color: semanticColors.typography.textSecondary,
  },
});

export default VisualizationModal;
